import { useState } from 'react';

import { AxiosError, AxiosResponse } from 'axios';
import { RDInstanceRequestStatus } from 'src/types/shared';

export interface UseRequestOptions<P> {
  maxRetries?: number;
  retryDelay?: number;
  initialParams?: P;
}

export interface UseRequestReturn<T, P> {
  status: RDInstanceRequestStatus;
  data: T | null;
  error: Error | AxiosError | null;
  execute: (params?: P, retryOptions?: { attemptLimit?: number }, executeStoreId?: number | string) => Promise<T | void>;
  retryCount: number;
  retryAttempts: number;
  maxRetries: number;
}

export const usePingRequest = <T, P = void>(
  requestFn: (params: P) => Promise<AxiosResponse<T>>,
  options: UseRequestOptions<P> = {},
): UseRequestReturn<T, P> => {
  const { maxRetries = 3, retryDelay = 1000, initialParams } = options;

  const [status, setStatus] = useState<RDInstanceRequestStatus>(RDInstanceRequestStatus.LOADING);
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | AxiosError | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);
  const [retryAttempts, setRetryAttempts] = useState<number>(0);

  const execute = async (params: P = initialParams as P, retryOptions: { attemptLimit?: number } = {}): Promise<T | void> => {
    const { attemptLimit = maxRetries } = retryOptions;

    setRetryCount(0);
    setRetryAttempts(0);
    setStatus(RDInstanceRequestStatus.LOADING);

    let currentRetry = 0;

    const attemptRequest = async (): Promise<T> => {
      try {
        const response = await requestFn(params);
        setData(response.data);
        setStatus(RDInstanceRequestStatus.SUCCESS);
        return response.data;
      } catch (e) {
        if (currentRetry < attemptLimit) {
          currentRetry++;
          setRetryCount(currentRetry);
          setRetryAttempts((prev) => prev + 1);
          setStatus(RDInstanceRequestStatus.RETRYING);

          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          return attemptRequest();
        } else {
          const errorRes = e as Error | AxiosError;
          setError(errorRes);
          setStatus(RDInstanceRequestStatus.ERROR);
          throw errorRes;
        }
      }
    };

    return attemptRequest();
  };

  return {
    status,
    data,
    error,
    execute,
    retryCount,
    retryAttempts,
    maxRetries,
  };
};
